// pages/DemoDeviceFrame.js
//
// Kleine "poortwachter" vóór de eigenlijke /demo-pagina (DemoDashboard.js):
// bepaalt op basis van de "device"-parameter (ingesteld vanuit Admin.js z'n
// Demo-tab, keuze Desktop/Mobiel) en de ECHTE viewportbreedte van wie de link
// opent, of de demo gewoon rechtstreeks getoond wordt (zoals voorheen) of
// binnen een grote, levensechte iPhone-mockup op een desktop-scherm.
//
// Waarom een losse iframe i.p.v. gewoon de mobiele CSS forceren: de bestaande
// mobiele stijlen in Dashboard.css/PropertyTimeline.css/... zijn allemaal
// "@media (max-width: ...)"-regels, en die reageren op de ECHTE
// browser-viewport, niet op de breedte van een omhullend <div>. Een <iframe>
// heeft daarentegen wél zijn eigen, onafhankelijke viewport - door die
// precies op iPhone-afmetingen te zetten activeren diezelfde bestaande
// media-queries daar vanzelf, en blijft het bovendien een echte, werkende
// pagina (scrollen/klikken werkt gewoon, geen statische screenshot).
//
// Opent iemand de link met device=mobile ECHT op een telefoon, dan toont dit
// gewoon de normale DemoDashboard rechtstreeks over het volledige scherm -
// een telefoon-mockup tonen op een telefoon zelf zou natuurlijk onzinnig zijn.
import React, { useState, useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import DemoDashboard from './DemoDashboard';
import './DemoDeviceFrame.css';

// Ruim boven de breedte van elke bestaande telefoon (ook de grootste, in
// landscape) maar ruim onder een echt tablet/laptop-scherm - zelfde soort
// grens als de mobiele media-queries elders in de app (700px), met wat
// marge.
const REAL_PHONE_MAX_WIDTH = 820;

function DemoDeviceFrame() {
  const [searchParams] = useSearchParams();
  const device = searchParams.get('device');
  const [viewportWidth, setViewportWidth] = useState(window.innerWidth);

  useEffect(() => {
    const onResize = () => setViewportWidth(window.innerWidth);
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, []);

  const isRealPhone = viewportWidth <= REAL_PHONE_MAX_WIDTH;
  const showMockup = device === 'mobile' && !isRealPhone;

  // Verbergt de (storende) verticale browser-scrollbalk enkel terwijl deze
  // losstaande mockup-pagina getoond wordt - zie de toelichting bij
  // "body.demo-phone-page-body" in DemoDeviceFrame.css.
  useEffect(() => {
    if (!showMockup) return undefined;
    document.body.classList.add('demo-phone-page-body');
    return () => document.body.classList.remove('demo-phone-page-body');
  }, [showMockup]);

  // Desktop-modus (of geen device-param, bv. oude gedeelde links) - gewoon
  // de bestaande demo tonen zoals altijd. Ook wie een "mobiel"-link écht op
  // een telefoon opent komt hier terecht (volledig scherm, geen mockup).
  if (!showMockup) {
    return <DemoDashboard />;
  }

  // Vanaf hier: iemand bekijkt een "mobiel"-demo op een desktop/laptop-
  // scherm - toon de iPhone-mockup met de demo er live in ingebed. Zelfde
  // instellingen doorgeven, enkel "device" eruit halen (anders zou de demo
  // in de iframe zelf weer proberen een geneste mockup te tonen).
  const innerParams = new URLSearchParams(searchParams);
  innerParams.delete('device');
  // Binnen de mockup is de fysieke telefoon zelf al het "dit is de app"-signaal
  // - de eigen SmartStays-wordmark bovenaan is dan overbodige ruimte-inname op
  // een toch al smal (390px) schermpje. Enkel in dit specifieke embed-geval
  // verbergen we 'm (zie nav-brand in DemoDashboard.js), niet voor gewone
  // bezoekers van /demo.
  innerParams.set('embedded', '1');
  const iframeSrc = `/demo?${innerParams.toString()}`;

  return (
    <div className="demo-phone-frame-page">
      <div className="demo-phone-frame">
        <div className="demo-phone-button demo-phone-button-power"></div>
        <div className="demo-phone-button demo-phone-button-volume-up"></div>
        <div className="demo-phone-button demo-phone-button-volume-down"></div>

        {/* Echte iOS-statusbalk (tijd links, signaal/wifi/batterij rechts) -
            een apart rijtje BOVEN de iframe (niet erover heen), zodat de
            eigen app-navbar (hamburger/logo/bel/profiel) er nooit onder
            verdwijnt. De Dynamic Island hierna floats erover, in dezelfde
            rij, zoals op een echt toestel. */}
        <div className="demo-phone-statusbar">
          <span className="demo-phone-time">9:41</span>
          <div className="demo-phone-status-icons">
            <svg width="18" height="12" viewBox="0 0 18 12" fill="currentColor" xmlns="http://www.w3.org/2000/svg">
              <rect x="0" y="8" width="3" height="4" rx="0.5"/>
              <rect x="4.5" y="6" width="3" height="6" rx="0.5"/>
              <rect x="9" y="3.5" width="3" height="8.5" rx="0.5"/>
              <rect x="13.5" y="0" width="3" height="12" rx="0.5"/>
            </svg>
            <svg width="16" height="12" viewBox="0 0 16 12" fill="none" xmlns="http://www.w3.org/2000/svg">
              <path d="M8 10.2C8.55 10.2 9 9.75 9 9.2C9 8.65 8.55 8.2 8 8.2C7.45 8.2 7 8.65 7 9.2C7 9.75 7.45 10.2 8 10.2Z" fill="currentColor"/>
              <path d="M5.1 6.9C5.98 6.1 7 5.65 8 5.65C9 5.65 10.02 6.1 10.9 6.9" stroke="currentColor" strokeWidth="1.3" strokeLinecap="round" fill="none"/>
              <path d="M2.3 4.1C4 2.55 5.95 1.75 8 1.75C10.05 1.75 12 2.55 13.7 4.1" stroke="currentColor" strokeWidth="1.3" strokeLinecap="round" fill="none"/>
            </svg>
            <svg width="25" height="12" viewBox="0 0 25 12" fill="none" xmlns="http://www.w3.org/2000/svg">
              <rect x="0.75" y="0.75" width="20.5" height="10.5" rx="2.5" stroke="currentColor" strokeWidth="1"/>
              <rect x="2.25" y="2.25" width="16" height="7.5" rx="1.2" fill="currentColor"/>
              <path d="M23 4V8C23.83 7.63 24.4 6.8 24.4 6C24.4 5.2 23.83 4.37 23 4Z" fill="currentColor"/>
            </svg>
          </div>
        </div>

        <div className="demo-phone-notch"></div>
        <iframe className="demo-phone-screen" src={iframeSrc} title="SmartStays demo (mobiel)" />
        <div className="demo-phone-home-indicator"></div>
      </div>
    </div>
  );
}

export default DemoDeviceFrame;
